import { useEffect, useMemo, useState } from 'react'
import {
  DAYS, NO_SERVICE, svcCapacity,
  gbp0, num0, gbpSigned, pct1, share1,
  perDay, variance, varianceColor, priorSeason, mergeRows, sumRows,
  type DetailPayload, type DetailBasis, type Variance, type MergedRow,
} from './researchShared'

// Ticket breakdown behind one clicked bar of the Research weekday charts.
//
// Layout is a wide three-block grid: this season | last season | variance.
// Every "£ / day" figure divides by the SLICE's running-day count, not by the
// days on which that particular item happened to sell — that is what makes the
// rows sum to the chart's bar and keeps the year-on-year comparison honest when
// the two years have different numbers of running days in the same slot.
//
// Each ticket row expands to the routes (Session labels) behind it, collated
// across departure times. Sales carrying no train session at all — walk-ups,
// untimed admissions and refunds — appear as an explicit
// "(not service-attributed)" row rather than being pro-rated away. That bucket
// is material (it can exceed 20% of a day) and can be negative.

export interface DetailCtx {
  dayNo: number
  y: number | null
  m: number | null
  wom: number | null
  label: string
}

const th: React.CSSProperties = {
  padding: '4px 8px', borderBottom: '1px solid #e5e1d6', color: '#6b6b6b',
  fontWeight: 600, whiteSpace: 'nowrap',
}
const td: React.CSSProperties = { padding: '3px 8px', borderBottom: '1px solid #f0ece1' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

/** The two variance cells (value then %), coloured green better / red worse. */
function VarianceCells({ v }: { v: Variance }) {
  const color = varianceColor(v)
  return (
    <>
      <td style={{ ...tdR, color, fontWeight: color ? 700 : 400 }}>{gbpSigned(v.delta)}</td>
      <td style={{ ...tdR, color, fontWeight: color ? 700 : 400 }}>
        {v.pct == null ? <span style={{ color: '#8a8a8a', fontWeight: 400 }}>new</span> : pct1(v.pct)}
      </td>
    </>
  )
}

export function TicketBreakdownModal({ ctx, onClose }: { ctx: DetailCtx; onClose: () => void }) {
  const [data, setData] = useState<DetailPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  useEffect(() => {
    let live = true
    const q = new URLSearchParams({ day_no: String(ctx.dayNo) })
    if (ctx.y != null) q.set('y', String(ctx.y))
    if (ctx.m != null) q.set('m', String(ctx.m))
    if (ctx.wom != null) q.set('wom', String(ctx.wom))
    fetch(`/api/research/weekday-revenue/detail?${q.toString()}`)
      .then(async (r) => {
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error((b as { error?: string }).error || `${r.status}`) }
        return r.json() as Promise<DetailPayload>
      })
      .then((d) => { if (live) { setData(d); setError(null) } })
      .catch((e) => { if (live) setError(String((e as Error)?.message ?? e)) })
    return () => { live = false }
  }, [ctx])

  // Close on Escape as well as backdrop click.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const cur: DetailBasis | null = data?.cur ?? null
  const prev: DetailBasis | null = data?.prev ?? null
  const curDays = cur?.days ?? 0
  const prevDays = prev?.days ?? 0

  const items = useMemo(
    () => (cur && prev
      ? mergeRows(cur.items, prev.items, (r) => r.item, (r) => r.qty, (r) => r.net)
      : []),
    [cur, prev])

  // Routes grouped by their parent ticket item, merged across both years.
  const routesByItem = useMemo(() => {
    const out = new Map<string, MergedRow[]>()
    if (!cur || !prev) return out
    const byItem = <T extends { item: string }>(list: T[]) => {
      const m = new Map<string, T[]>()
      for (const r of list) { const a = m.get(r.item) ?? []; a.push(r); m.set(r.item, a) }
      return m
    }
    const curBy = byItem(cur.routes)
    const prevBy = byItem(prev.routes)
    for (const item of new Set([...curBy.keys(), ...prevBy.keys()])) {
      out.set(item, mergeRows(
        curBy.get(item) ?? [], prevBy.get(item) ?? [],
        (r) => r.route, (r) => r.qty, (r) => r.net,
      ))
    }
    return out
  }, [cur, prev])

  const services = useMemo(
    () => (cur && prev
      ? mergeRows(
        cur.services, prev.services,
        (r) => `${r.direction}|${r.dep_time}|${r.service}`,
        (r) => r.qty, (r) => r.net)
      : []),
    [cur, prev])

  const totals = useMemo(() => sumRows(items), [items])

  const toggle = (key: string) => setExpanded((s) => {
    const next = new Set(s)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })

  const season = data?.season ?? ''
  const lastSeason = season ? priorSeason(season) : ''
  const totalVar = variance(perDay(totals.curNet, curDays), perDay(totals.prevNet, prevDays), prevDays)

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: '16px 18px',
          width: 1240, maxWidth: '96vw', height: '90vh', maxHeight: '90vh',
          overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <strong style={{ fontSize: '1rem' }}>Ticket breakdown — {ctx.label}</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#6b6b6b', lineHeight: 1 }}>×</button>
        </div>

        {error && <p style={{ color: '#a33', fontSize: '0.85rem' }}>{error}</p>}
        {!data && !error && <p style={{ fontSize: '0.85rem', color: '#6b6b6b' }}>Loading…</p>}

        {data && (
          <>
            <p style={{ fontSize: '0.72rem', color: '#8a8a8a', margin: '0 0 10px' }}>
              Comparing <strong>{season}</strong> ({curDays} running {curDays === 1 ? 'day' : 'days'}) against{' '}
              <strong>{lastSeason}</strong> ({prevDays} running {prevDays === 1 ? 'day' : 'days'}) for the same slot.
              All variances are on a <strong>per running day</strong> basis, so a different number of running days
              between the two years doesn&apos;t distort the comparison.
              {' '}Click <span style={{ fontWeight: 700 }}>▸</span> on a ticket row to see the routes behind it.
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>Ticket / item</th>
                  <th style={{ ...th, textAlign: 'right' }} rowSpan={2}>Share</th>
                  <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }} colSpan={3}>This year — {season}</th>
                  <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6' }} colSpan={3}>Last year — {lastSeason}</th>
                  <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }} colSpan={2}>Variance / running day</th>
                </tr>
                <tr>
                  <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }}>Tickets</th>
                  <th style={{ ...th, textAlign: 'right', background: '#fbf9f4' }}>Net £</th>
                  <th style={{ ...th, textAlign: 'right', background: '#fbf9f4' }}>£ / day</th>
                  <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6' }}>Tickets</th>
                  <th style={{ ...th, textAlign: 'right' }}>Net £</th>
                  <th style={{ ...th, textAlign: 'right' }}>£ / day</th>
                  <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }}>Δ £ / day</th>
                  <th style={{ ...th, textAlign: 'right', background: '#fbf9f4' }}>Δ %</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const v = variance(perDay(r.curNet, curDays), perDay(r.prevNet, prevDays), prevDays)
                  const routes = routesByItem.get(r.label) ?? []
                  const isOpen = expanded.has(r.label)
                  const hasRoutes = routes.length > 0
                  return [
                    <tr key={r.key} style={isOpen ? { background: '#faf8f2' } : undefined}>
                      <td style={td}>
                        <button
                          type="button"
                          onClick={() => hasRoutes && toggle(r.label)}
                          disabled={!hasRoutes}
                          aria-expanded={isOpen}
                          title={hasRoutes ? 'Show the routes behind this ticket type' : 'No service breakdown for this line'}
                          style={{
                            background: 'none', border: 'none', cursor: hasRoutes ? 'pointer' : 'default',
                            color: hasRoutes ? '#0b3d2e' : '#d4cfc2', fontWeight: 700, fontSize: '0.8rem',
                            padding: '0 6px 0 0', width: 18,
                          }}
                        >{isOpen ? '▾' : '▸'}</button>
                        {r.label}
                      </td>
                      <td style={tdR}>{totals.curNet !== 0 ? share1((r.curNet / totals.curNet) * 100) : '—'}</td>
                      <td style={{ ...tdR, borderLeft: '1px solid #f0ece1' }}>{r.curQty ? num0(r.curQty) : '—'}</td>
                      <td style={tdR}>{gbp0(r.curNet)}</td>
                      <td style={tdR}>{curDays > 0 ? gbp0(perDay(r.curNet, curDays)) : '—'}</td>
                      <td style={{ ...tdR, borderLeft: '1px solid #f0ece1', color: '#6b6b6b' }}>{r.prevQty ? num0(r.prevQty) : '—'}</td>
                      <td style={{ ...tdR, color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(r.prevNet) : '—'}</td>
                      <td style={{ ...tdR, color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(perDay(r.prevNet, prevDays)) : '—'}</td>
                      <VarianceCells v={v} />
                    </tr>,
                    ...(isOpen ? routes.map((rt) => {
                      const rv = variance(perDay(rt.curNet, curDays), perDay(rt.prevNet, prevDays), prevDays)
                      const unattributed = rt.label === NO_SERVICE
                      return (
                        <tr key={`${r.key}::${rt.key}`} style={{ background: '#faf8f2' }}>
                          <td style={{ ...td, paddingLeft: 32, fontStyle: unattributed ? 'italic' : undefined, color: unattributed ? '#8a6d1a' : '#333' }}>
                            ↳ {unattributed ? 'Not service-attributed (walk-up / untimed / refunds)' : rt.label}
                          </td>
                          <td style={{ ...tdR, fontWeight: 600 }}>{r.curNet !== 0 ? share1((rt.curNet / r.curNet) * 100) : '—'}</td>
                          <td style={{ ...tdR, borderLeft: '1px solid #f0ece1' }}>{rt.curQty ? num0(rt.curQty) : '—'}</td>
                          <td style={tdR}>{gbp0(rt.curNet)}</td>
                          <td style={tdR}>{curDays > 0 ? gbp0(perDay(rt.curNet, curDays)) : '—'}</td>
                          <td style={{ ...tdR, borderLeft: '1px solid #f0ece1', color: '#6b6b6b' }}>{rt.prevQty ? num0(rt.prevQty) : '—'}</td>
                          <td style={{ ...tdR, color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(rt.prevNet) : '—'}</td>
                          <td style={{ ...tdR, color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(perDay(rt.prevNet, prevDays)) : '—'}</td>
                          <VarianceCells v={rv} />
                        </tr>
                      )
                    }) : []),
                  ]
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e5e1d6' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 700 }}>Total</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none' }}>100.0%</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none', borderLeft: '1px solid #f0ece1' }}>{num0(totals.curQty)}</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none' }}>{gbp0(totals.curNet)}</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none' }}>{curDays > 0 ? gbp0(perDay(totals.curNet, curDays)) : '—'}</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none', borderLeft: '1px solid #f0ece1', color: '#6b6b6b' }}>{prevDays > 0 ? num0(totals.prevQty) : '—'}</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none', color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(totals.prevNet) : '—'}</td>
                  <td style={{ ...tdR, fontWeight: 700, border: 'none', color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(perDay(totals.prevNet, prevDays)) : '—'}</td>
                  <VarianceCells v={totalVar} />
                </tr>
              </tfoot>
            </table>

            {services.length > 0 && (
              <>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', margin: '18px 0 2px' }}>
                  Running services ({services.length}) — revenue by departure
                </div>
                <p style={{ fontSize: '0.7rem', color: '#8a8a8a', margin: '0 0 6px' }}>
                  Session-tied sales grouped by departure. Capacity is indicative — standard rake 7 × 64 = 448 seats, Autocar 48.
                  Load is this year&apos;s pax per running day against capacity.
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>Dir</th>
                      <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>Depart</th>
                      <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>Service</th>
                      <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }} colSpan={2}>This year</th>
                      <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6' }} colSpan={2}>Last year</th>
                      <th style={{ ...th, textAlign: 'center', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }} colSpan={2}>Variance / day</th>
                      <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6' }} rowSpan={2}>Capacity</th>
                      <th style={{ ...th, textAlign: 'right' }} rowSpan={2}>Load</th>
                    </tr>
                    <tr>
                      <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }}>Pax / day</th>
                      <th style={{ ...th, textAlign: 'right', background: '#fbf9f4' }}>£ / day</th>
                      <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6' }}>Pax / day</th>
                      <th style={{ ...th, textAlign: 'right' }}>£ / day</th>
                      <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e5e1d6', background: '#fbf9f4' }}>Δ £ / day</th>
                      <th style={{ ...th, textAlign: 'right', background: '#fbf9f4' }}>Δ %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((s) => {
                      const [direction, depTime, service] = s.label.split('|')
                      const cap = svcCapacity(service)
                      const paxDay = perDay(s.curQty, curDays)
                      const v = variance(perDay(s.curNet, curDays), perDay(s.prevNet, prevDays), prevDays)
                      return (
                        <tr key={s.key}>
                          <td style={td}>{direction === 'Northbound' ? 'N' : 'S'}</td>
                          <td style={{ ...td, fontWeight: 700 }}>{depTime}</td>
                          <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={service}>{service || '(standard service)'}</td>
                          <td style={{ ...tdR, borderLeft: '1px solid #f0ece1' }}>{num0(paxDay)}</td>
                          <td style={tdR}>{gbp0(perDay(s.curNet, curDays))}</td>
                          <td style={{ ...tdR, borderLeft: '1px solid #f0ece1', color: '#6b6b6b' }}>{prevDays > 0 ? num0(perDay(s.prevQty, prevDays)) : '—'}</td>
                          <td style={{ ...tdR, color: '#6b6b6b' }}>{prevDays > 0 ? gbp0(perDay(s.prevNet, prevDays)) : '—'}</td>
                          <VarianceCells v={v} />
                          <td style={{ ...tdR, borderLeft: '1px solid #f0ece1' }}>{cap}</td>
                          <td style={tdR}>{cap > 0 ? `${Math.round((paxDay / cap) * 100)}%` : '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** Human label for a weekday number (1 = Mon). */
export const dayLabel = (dayNo: number): string => DAYS[dayNo - 1]
